import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";

interface Props {
    /** Where "see the session" goes. */
    sessionId: string;
    /**
     * Whether a recording is on its way to the pipeline. When it is not, there
     * is nothing to wait for and nothing will arrive — promising notes anyway
     * would leave the therapist watching for something that is not coming.
     */
    recording: boolean;
    onOpenSession: () => void;
    onGoToSessions: () => void;
}

/**
 * What a finished meeting leaves on screen: the same promise the audio upload
 * and the live session make.
 *
 * A therapist who has just said goodbye to a patient should be told that the
 * notes are coming, not dropped on an empty session page that looks like the
 * recording went nowhere. It reuses `session.audio.*` word for word, because
 * the three routes into the pipeline are one thing from here on and reading
 * differently would suggest otherwise.
 */
export default function MeetingProcessingNotice({
    sessionId,
    recording,
    onOpenSession,
    onGoToSessions,
}: Props) {
    const { t } = useTranslation();

    return (
        <div
            className="flex flex-1 flex-col items-center justify-center gap-6 p-8 text-center"
            data-testid="meeting-processing"
            data-session-id={sessionId}
        >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-room-ok/30 bg-room-ok-tint">
                <CheckCircle2 className="h-7 w-7 text-room-ok" aria-hidden="true" />
            </div>

            <h2 className="text-xl font-semibold tracking-tight text-room-ink">
                {recording
                    ? t("session.audio.processingHeader")
                    : t("session.alchelyMeeting.finishedHeader")}
            </h2>

            {/* This paragraph is why the room tokens exist. It used to be
                `text-content-tertiary`, which is theme-dependent — so the line
                telling a therapist their notes are coming measured 7.82:1 in
                dark mode and 4.05:1 in light, a 1.4.3 failure that only half
                the users ever saw. */}
            <p className="max-w-md text-sm leading-relaxed text-room-ink-muted">
                {recording
                    ? t("session.audio.processingDescription")
                    : t("session.alchelyMeeting.finishedDescription")}
            </p>

            <div className="flex w-full max-w-xs flex-col gap-3 pt-2">
                {/* The two actions stop competing: seeing the session is what
                    the therapist came here for, and the list is the way out. */}
                <button
                    type="button"
                    onClick={onOpenSession}
                    className="room-control room-control-primary w-full"
                >
                    {t("session.alchelyMeeting.openSession")}
                </button>
                <button
                    type="button"
                    onClick={onGoToSessions}
                    className="room-control w-full"
                >
                    {t("session.audio.goToDashboard")}
                </button>
            </div>
        </div>
    );
}
